import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { MapPin } from "lucide-react";
import type { ProvinceRegion } from "@/content/provinces-th/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

const REGIONS: ProvinceRegion[] = [
  "central",
  "north",
  "northeast",
  "east",
  "west",
  "south",
];

type ProvinceItem = { slug: string; nameTh: string };
type ProvincesLoaderData = {
  regionLabel: Record<ProvinceRegion, string>;
  byRegion: Record<ProvinceRegion, ProvinceItem[]>;
  all: ProvinceItem[];
};

export const Route = createFileRoute("/provinces/")({
  loader: async (): Promise<ProvincesLoaderData> => {
    const { PROVINCES_BY_REGION, REGION_LABEL, THAI_PROVINCES } = await import(
      "@/content/provinces-th/registry"
    );
    const project = (list: readonly { slug: string; nameTh: string }[]) =>
      list.map((p) => ({ slug: p.slug, nameTh: p.nameTh }));
    return {
      regionLabel: { ...REGION_LABEL } as Record<ProvinceRegion, string>,
      byRegion: Object.fromEntries(
        REGIONS.map((r) => [r, project(PROVINCES_BY_REGION[r])]),
      ) as Record<ProvinceRegion, { slug: string; nameTh: string }[]>,
      all: project(THAI_PROVINCES),
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const provinces = loaderData?.all ?? [];
    const base = pageHeadForProvince({
      title: "บริการ Notary & แปลรับรอง — ครบ 77 จังหวัด | Thai Notary",
      description:
        "บริการ Notary Public, แปลรับรอง, MFA และวีซ่า ครอบคลุมครบ 77 จังหวัดทั่วประเทศไทย — รับ-ส่งเอกสารถึงบ้าน เริ่มต้น 1,500 บาท",
      path: "/provinces",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/provinces")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "77 จังหวัด", url: `${site}/provinces` },
            ]),
          ),
        },
        ...(provinces.length
          ? [{
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "บริการครบ 77 จังหวัด — Thai Notary",
            numberOfItems: provinces.length,
            itemListElement: provinces.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/provinces/${p.slug}`,
              name: p.nameTh,
            })),
          }),
          }]
          : []),
      ],
    };
  },
  component: ProvincesIndex,
});

function ProvincesIndex() {
  const { regionLabel, byRegion } = Route.useLoaderData() as ProvincesLoaderData;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-6xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">77 จังหวัด</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              Coverage · ทั่วประเทศไทย
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              บริการครบ 77 จังหวัด — รับ-ส่งเอกสารถึงบ้านทั่วประเทศ
            </h1>
            <p id="ai-answer-tldr" className="mt-4 max-w-3xl rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">สรุปสั้น</span>
              Thai Notary ให้บริการ Notary Public, แปลรับรอง 30+ ภาษา, MFA Consular Legalization
              และวีซ่า ครอบคลุมทั้ง <strong>77 จังหวัด</strong> ผ่านสำนักงานในกรุงเทพและภูเก็ต
              พร้อมทีมพันธมิตรในเชียงใหม่ ขอนแก่น และหาดใหญ่ — รับ-ส่งเอกสารถึงบ้านด้วย Kerry/Flash
              ภายใน 1–3 วันทำการ
            </p>
          </div>
        </section>

        {REGIONS.map((region) => (
          <section key={region} className="border-b border-border">
            <div className="mx-auto max-w-6xl px-4 py-10">
              <h2 className="text-2xl font-bold">
                {regionLabel[region]}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  ({byRegion[region].length} จังหวัด)
                </span>
              </h2>
              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {byRegion[region].map((p) => (
                  <Link
                    key={p.slug}
                    to="/provinces/$province"
                    params={{ province: p.slug }}
                    className="group flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-3 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-gold" />
                    <span className="font-semibold text-foreground group-hover:text-gold">
                      {p.nameTh}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-3xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">
              ไม่ว่าคุณอยู่จังหวัดไหน เราก็ดูแลให้
            </h2>
            <p className="mt-3 text-sm opacity-90">
              ทักไลน์ส่งภาพเอกสารและจังหวัดที่คุณอยู่ — ทีมประเมินภายใน 30 นาที
              พร้อมแจ้งค่าเดินเอกสารและเวลาส่งคืน
            </p>
            <Link
              to="/contact"
              className="mt-5 inline-flex rounded-md bg-gold px-6 py-3 text-sm font-bold text-foreground"
            >
              ติดต่อทีมงาน
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
